import type { ReactNode, Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import type { StatusSeverity } from './statusSeverity';
import { resolveTone } from './statusSeverity';

export type InlineNoteTone = Extract<StatusSeverity, 'success' | 'warning' | 'info' | 'neutral'>;

export interface InlineNoteProps {
  tone?: InlineNoteTone;
  /** Override the default tone icon. */
  icon?: ReactNode;
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

const DEFAULT_ICON: Record<InlineNoteTone, typeof InfoOutlined> = {
  success: CheckCircleOutlined,
  warning: WarningAmberRounded,
  info: InfoOutlined,
  neutral: InfoOutlined,
};

/**
 * Toned inline callout (the Polaris kit `WzNote`) — a tinted rounded box with a
 * leading tone icon, used for eligibility / approval / package hints in the
 * request forms. Copy-agnostic: the caller supplies translated `children`.
 */
export function InlineNote({ tone = 'info', icon, children, ref }: InlineNoteProps) {
  const theme = useTheme();
  const t = resolveTone(theme, tone);
  const Icon = DEFAULT_ICON[tone];

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: '10px 14px',
        borderRadius: 1.5,
        bgcolor: alpha(t.main, 0.09),
        border: `1px solid ${alpha(t.main, 0.32)}`,
      }}
    >
      <Box aria-hidden sx={{ flexShrink: 0, mt: '1px', color: t.dark, display: 'inline-flex' }}>
        {icon ?? <Icon sx={{ fontSize: 16 }} />}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: t.dark, lineHeight: 1.5 }}>
        {children}
      </Typography>
    </Box>
  );
}
